import MiniSearch, { SearchOptions, SearchResult } from 'minisearch'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import { normalizeText } from 'modules/core/lib/stringUtils'

type MiniSearchParameters = ConstructorParameters<typeof MiniSearch>[0]
type UseTextSearchOptions<T, U, EU> = Pick<MiniSearchParameters, 'fields' | 'storeFields'> & {
  fields: Array<keyof T>
  storeFields: U
  extraFields?: string[]
  extraStoreFields?: EU
  extractField?: (document: T, fieldName: string) => string
  minScore?: number
  skipIndexing?: boolean
  combineWith?: 'OR' | 'AND'
} & Pick<SearchOptions, 'fuzzy' | 'boost'>

const processTerm = (term: string) => normalizeText(term)

/**
 *  This hook enables using the `minisearch` npm library to search any collection in a performant way.
 *  Its best use case is prefix/fuzzy search in environments where collections are potentially big.
 *  The tradeoff when using it is that there's a tiny performance penalty on book time as the search
 *  needs to be indexed.
 *
 *  @param collection - A collection that will be used as source data.
 *  @param options.fields - The names of the fields will be used in the search.
 *  @param options.storefields - The fields that will be returned with each result object.
 *
 *  @returns - An object containing the `search` method that takes in a search term and returns a
 *             collection of Array<T>.
 */
export function useTextSearch<
  T extends { id: unknown },
  U extends Array<keyof T>,
  EU extends string[]
>(
  collection: T[],
  search: string,
  {
    fields,
    storeFields,
    extraFields,
    extraStoreFields,
    extractField,
    boost,
    fuzzy,
    minScore,
    skipIndexing,
    combineWith,
  }: UseTextSearchOptions<T, U, EU>
) {
  const miniSearch = useLazyRef<MiniSearch<T>>(
    () =>
      new MiniSearch<T>({
        fields: fields.concat(extraFields ?? []),
        storeFields: storeFields.concat(extraStoreFields ?? []),
        extractField:
          extractField ?? ((document, fieldName) => document[fieldName as keyof T] as string),
        processTerm,
        searchOptions: {
          processTerm,
        },
      })
  )

  useEffect(() => {
    miniSearch.current.removeAll()

    const filteredCollection = collection.filter(({ id }) => id !== null && id !== undefined)

    if (filteredCollection.length === 0 || skipIndexing) return

    miniSearch.current.addAll(filteredCollection)
  }, [skipIndexing])

  const results = useMemo(() => {
    return miniSearch.current.search(search, {
      prefix: true,
      boost,
      fuzzy,
      filter: (result) => result.score > (minScore || 0.1),
      combineWith,
    }) as unknown as Array<
      Pick<SearchResult, 'id' | 'score'> & Pick<T, U[number]> & { [K in EU[number]]: any }
    >
  }, [search])

  return { results }
}
